import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useRef, useCallback } from 'react';
import { ServerDataGrid, Grid, Stack, SearchField, Checkbox, FormControlLabel, Select, Button, ButtonContainer, MenuItem, useSnackbar, Spacer, Tooltip, Icon, Modal, ModalBody, ModalFooter, Typography, useTheme } from '@emburse/embark-core';
import { SyncIcon } from '@emburse/embark-icons';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import { $t, useTranslation } from '../../hooks/useTranslation';
import { formatDateTime, isValidDate } from '../../util/dateAdapter';
import { defaultPagination } from '../../interfaces/EmburseAccountSync';
import { getUsers, syncEmployees } from '../../api/emburseAccountSync';
function EmburseAccountSyncManager() {
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [data, setData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [companyFilter, setCompanyFilter] = useState('');
    const [responseCodeFilter, setResponseCodeFilter] = useState(null);
    const [shardIDFilter, setShardIDFilter] = useState(0);
    const [showOnlyRecentUsersFilter, setShowOnlyRecentUsersFilter] = useState(true);
    const [page, setPage] = useState(defaultPagination.page);
    const [pageSize, setPageSize] = useState(defaultPagination.pageSize);
    const [sortModel, setSortModel] = useState(defaultPagination.sort);
    const theme = useTheme();
    const dgRef = useRef();
    useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const handleError = useCallback((err) => {
        enqueueSnackbar(err.Message ? err.Message : err, {
            variant: 'alert',
            AlertProps: { severity: 'error' }
        });
    }, [enqueueSnackbar]);
    const loadEmburseAccountSyncData = useCallback(async (newPage, newPageSize, newSortModel) => {
        const options = {
            ShardID: shardIDFilter,
            Search: companyFilter,
            ResponseCode: responseCodeFilter ? parseInt(responseCodeFilter, 10) : 0,
            Pagination: {
                page: newPage + 1,
                pageSize: newPageSize,
                sort: newSortModel?.length > 0 ? newSortModel[0] : defaultPagination.sort[0],
                cache: defaultPagination.cache
            },
            IncludeOldAccounts: !showOnlyRecentUsersFilter
        };
        if (page !== newPage) {
            setPage(newPage);
        }
        if (pageSize !== newPageSize) {
            setPageSize(newPageSize);
        }
        if (newSortModel && sortModel !== newSortModel) {
            setSortModel(newSortModel);
        }
        setLoading(true);
        try {
            const { Count, Results } = await getUsers(options);
            const rowData = Results.map((row) => ({
                ...row,
                id: `${row.UserID}_${row.Usr_Email}`,
                RowID: `${row.UserID}_${row.Usr_Email}`,
                SelectedForSync: false
            }));
            if (dgRef.current) {
                dgRef.current.setRowCount(Count);
            }
            setData(rowData);
            setSelectedRows([]);
            setLoading(false);
        }
        catch (error) {
            setData([]);
            handleError(error);
        }
    }, [
        shardIDFilter,
        companyFilter,
        responseCodeFilter,
        showOnlyRecentUsersFilter,
        page,
        pageSize,
        sortModel,
        handleError
    ]);
    const syncSelectedUsers = useCallback(async (selectedRowsForSync) => {
        try {
            if (selectedRowsForSync.length > 25) {
                setModalOpen(true);
                return;
            }
            setLoading(true);
            const employeePKsForSync = selectedRowsForSync.map((user) => user.UserID);
            const { Status, Message } = await syncEmployees(employeePKsForSync, shardIDFilter);
            if (Status !== 'success') {
                throw new Error(Message);
            }
            loadEmburseAccountSyncData(page, pageSize, sortModel);
            enqueueSnackbar($t(`${selectedRows.length > 1 ? 'Users were' : 'User was'} synced successfully.`), { variant: 'success' });
        }
        catch (error) {
            handleError(error);
            setLoading(false);
        }
    }, [
        enqueueSnackbar,
        handleError,
        loadEmburseAccountSyncData,
        page,
        pageSize,
        selectedRows.length,
        shardIDFilter,
        sortModel
    ]);
    useEffect(() => {
        loadEmburseAccountSyncData(page, pageSize, sortModel);
    }, [
        loadEmburseAccountSyncData,
        page,
        pageSize,
        shardIDFilter,
        showOnlyRecentUsersFilter,
        sortModel
    ]);
    const selectedRowHandler = useCallback((e, emburseAccountSync) => {
        const rowID = `${emburseAccountSync.UserID}_${emburseAccountSync.Usr_Email}`;
        const selectedDataRow = {
            ...emburseAccountSync,
            SelectedForSync: e.target.checked
        };
        if (e.target.checked) {
            setSelectedRows((prevState) => [...prevState, selectedDataRow]);
        }
        else {
            setSelectedRows((currentlySelectedRows) => currentlySelectedRows.filter((row) => row.RowID !== rowID));
        }
        setData((prevState) => prevState.map((row) => row.RowID === rowID ? selectedDataRow : row));
    }, []);
    const shardIDChangeHandler = useCallback((e) => {
        setShardIDFilter(parseInt(e.target.value, 10));
    }, []);
    const companySearchChangeHandler = useCallback((val) => {
        setCompanyFilter(val);
    }, []);
    const responseCodeFilterChangeHandler = useCallback((val) => {
        if (val.toLocaleLowerCase() === 'null') {
            setResponseCodeFilter(null);
        }
        else {
            setResponseCodeFilter(val.length > 0 ? val : '');
        }
    }, []);
    const showOnlyRecentUsersChangeHandler = useCallback((e, value) => {
        setShowOnlyRecentUsersFilter(value);
    }, []);
    const columns = [
        {
            field: 'Select',
            headerName: '',
            width: 80,
            align: 'center',
            sortable: false,
            renderCell: (cell) => (_jsx("div", { style: {
                    width: '100%',
                    textAlign: 'center'
                }, children: _jsx(Checkbox, { color: "primary", checked: cell.row.SelectedForSync, onChange: (e) => {
                        selectedRowHandler(e, { ...cell.row });
                    } }) }))
        },
        {
            field: 'ShardID',
            headerName: $t('Shard ID'),
            width: 100,
            align: 'center',
            sortable: false,
            renderCell: (cell) => _jsx(Typography, { children: cell.value })
        },
        {
            field: 'Company_Name',
            headerName: $t('Company Name'),
            width: 300,
            sortable: !loading,
            renderCell: (cell) => _jsx(Typography, { children: cell.value })
        },
        {
            field: 'Usr_Email',
            headerName: $t('User Email'),
            width: 300,
            sortable: !loading,
            renderCell: (cell) => _jsx(Typography, { children: cell.value })
        },
        {
            field: 'EmburseAccountSync_LastAttempt_Date',
            headerName: $t('Last Sync Attempt'),
            width: 250,
            sortable: !loading,
            renderCell: (cell) => (_jsx(Typography, { children: isValidDate(new Date(cell.value))
                    ? formatDateTime(new Date(cell.value))
                    : null }))
        },
        {
            field: 'EmburseAccountSync_LastAttempt_ResponseCode',
            headerName: $t('Last Sync Response'),
            width: 250,
            sortable: !loading,
            renderCell: (cell) => _jsx(Typography, { children: cell.value })
        }
    ];
    return (_jsxs(SingleColumnLayout, { children: [_jsx(Modal, { open: modalOpen, onClose: () => setModalOpen(false), modalHeader: "Exceeded Maximum Users For Sync", modalFooter: _jsx(ModalFooter, { children: _jsx(ButtonContainer, { alignButtons: "right", children: _jsx(Button, { onClick: () => {
                                setModalOpen(false);
                            }, children: $t('Change Selection') }) }) }), children: _jsx(ModalBody, { children: _jsxs(Grid, { container: true, direction: "column", children: [_jsx(Grid, { item: true, children: _jsx(Typography, { children: $t('Please reduce selection to no more than 25 users.') }) }), _jsx(Grid, { item: true, children: _jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", children: [_jsx(Typography, { children: $t('Number of selected users:') }), _jsx(Typography, { style: { fontWeight: 'bold', color: theme.palette.error.main }, children: selectedRows.length })] }) })] }) }) }), _jsx(Stack, { spacing: 1, direction: "row", children: _jsx(Typography, { variant: "h4", gutterBottom: true, children: $t('Emburse Account Sync Manager') }) }), _jsx(Spacer, { size: 32 }), _jsxs(Stack, { spacing: 1, direction: "row", justifyContent: "space-between", alignItems: "flex-start", children: [_jsxs(Stack, { spacing: 1, direction: "row", alignSelf: "flex-start", children: [_jsx(Select, { label: $t('Shard ID'), id: "ShardIDFilter", defaultValue: 0, disabled: loading, onChange: shardIDChangeHandler, children: [0, 1, 2, 3, 4].map((x) => (_jsx(MenuItem, { name: `Shard Id ${x}`, value: x, children: x }, x))) }), _jsx(SearchField, { label: $t('Company'), id: "CompanyFilter", disabled: loading, doSearchOnChange: true, onChangeTimeout: 250, searchAction: companySearchChangeHandler }), _jsx(SearchField, { id: "responseCodeFilter", name: "responseCodeFilter", label: $t('Response Code'), disabled: loading, doSearchOnChange: true, onChangeTimeout: 250, searchAction: responseCodeFilterChangeHandler }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { id: "ShowOnlyRecentUsersFilter", checked: showOnlyRecentUsersFilter, onChange: showOnlyRecentUsersChangeHandler }), labelPlacement: "end", label: _jsx(Stack, { direction: "row", spacing: 1, alignItems: "center", children: _jsx(Tooltip, { placement: "top-start", title: `${$t('Show only users who have attempted to sync in the last 2 years.')}`, children: _jsx(Typography, { children: $t('Filter Recent Users') }) }) }) })] }), _jsx(Stack, { alignSelf: "flex-end", children: _jsx(Button, { "aria-label": "userSync", startIcon: _jsx(Icon, { iconUrl: SyncIcon }), disabled: selectedRows.length === 0 || loading, onClick: () => {
                                syncSelectedUsers(selectedRows);
                            }, children: $t('Sync Users') }) })] }), _jsx(Spacer, { size: 24 }), _jsx(ServerDataGrid, { functionRef: dgRef, rowCount: 0, autoHeight: true, loading: loading, onUpdated: loadEmburseAccountSyncData, rows: data, columns: columns, pageSizeOptions: [25, 50, 100], localeText: {
                    noRowsLabel: $t('There are no Accounts matching the current filters.')
                } })] }));
}
export default EmburseAccountSyncManager;
